import { Link } from 'react-router-dom';
import { Users, Shield, Zap, BarChart3, ArrowLeft, AlertTriangle } from 'lucide-react';

export default function BlogUnlockingCopyTrading() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-600/10 border border-primary-500/30 text-xs text-primary-200 uppercase tracking-wide">
            <Users className="w-3 h-3" />
            <span>Copy trading</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Unlocking the Power of Copy Trading on Polymarket with BetMimic
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Copy trading lets you mirror the moves of top performers without needing to be a prediction market expert.
            BetMimic is built specifically for Polymarket users: non-custodial, focused on clean execution, and designed
            to help you automate responsibly.
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-dark-400">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-primary-300 hover:text-primary-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
            <span className="hidden sm:inline">·</span>
            <span>10–14 min read</span>
          </div>
        </header>

        <section className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 text-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 mt-0.5" />
            <div>
              <div className="font-semibold">Risk disclosure</div>
              <div className="mt-1 text-red-200/90">
                Prediction markets and automation can lead to rapid losses. BetMimic is not a broker, exchange, custodian,
                or investment adviser. You are solely responsible for your trades.
              </div>
            </div>
          </div>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">What is copy trading and why use it on Polymarket?</h2>
          <p className="text-sm text-dark-300">
            Copy trading is a strategy where you mirror a trader&apos;s actions automatically. On Polymarket, traders buy and
            sell <span className="font-medium">YES</span> or <span className="font-medium">NO</span> shares that represent
            probabilities of real-world outcomes.
          </p>
          <p className="text-sm text-dark-300">
            The appeal is simple: top wallets often have better timing, better discipline, and more reps. But copying them
            manually is time-consuming and error-prone. Copy trading automates the boring part while letting you layer your
            own risk controls on top.
          </p>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            How BetMimic works (step-by-step)
          </h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-2">
            <li>
              <span className="font-medium">Sign up and connect your wallet</span> – Create an account and connect your
              Polymarket wallet. BetMimic is non-custodial; you keep control.
            </li>
            <li>
              <span className="font-medium">Browse and select traders</span> – Use the dashboard to identify traders by
              metrics like PnL, win rate, and category focus.
            </li>
            <li>
              <span className="font-medium">Set up a copy configuration</span> – Choose sizing rules and risk parameters:
              max exposure, limits, and filters for specific markets.
            </li>
            <li>
              <span className="font-medium">Automate execution</span> – BetMimic routes orders through Polymarket&apos;s
              official CLOB, with guardrails and timeouts.
            </li>
            <li>
              <span className="font-medium">Monitor and optimize</span> – Track positions and fills, and watch for drift
              (differences from the original trade). Adjust configs when needed.
            </li>
          </ol>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            Key features that set BetMimic apart
          </h2>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Non-custodial security</span> – You keep full wallet control.
            </li>
            <li>
              <span className="font-medium">Real-time monitoring</span> – Tracks positions, PnL, and fills.
            </li>
            <li>
              <span className="font-medium">Risk tools</span> – Limits, filters, and guardrails to protect capital.
            </li>
            <li>
              <span className="font-medium">Community support</span> – Get help and updates in Discord.
            </li>
            <li>
              <span className="font-medium">Educational resources</span> – Guides from basic setup to advanced operating
              playbooks.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-400" />
            Practical strategies to maximize your success
          </h2>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Start small</span> – Begin with 1–2% allocations so you learn the trader&apos;s
              style.
            </li>
            <li>
              <span className="font-medium">Diversify</span> – Copy multiple traders across categories.
            </li>
            <li>
              <span className="font-medium">Use filters</span> – Prefer liquid markets to reduce slippage.
            </li>
            <li>
              <span className="font-medium">Monitor regularly</span> – Review performance weekly.
            </li>
            <li>
              <span className="font-medium">Exit wisely</span> – Use take-profit and stop-loss rules to avoid chop.
            </li>
          </ul>

          <div className="overflow-x-auto border border-dark-700 rounded-xl">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-dark-800/80 text-dark-200">
                <tr>
                  <th className="px-4 py-3 font-semibold">Style</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold">Risk level</th>
                  <th className="px-4 py-3 font-semibold">Best for</th>
                  <th className="px-4 py-3 font-semibold">Key config tips</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700 text-dark-300">
                <tr>
                  <td className="px-4 py-3 font-medium text-white">Conservative</td>
                  <td className="px-4 py-3">Mirror steady, lower-volatility traders with smaller allocations.</td>
                  <td className="px-4 py-3">Low</td>
                  <td className="px-4 py-3">Beginners / risk-averse users</td>
                  <td className="px-4 py-3">1–2% per trade, focus liquid markets, take profits earlier.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-white">Balanced</td>
                  <td className="px-4 py-3">Blend 3–5 traders for diversified exposure.</td>
                  <td className="px-4 py-3">Medium</td>
                  <td className="px-4 py-3">General users seeking growth</td>
                  <td className="px-4 py-3">Equal sizing, rebalance monthly, ignore illiquid markets.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-white">Aggressive</td>
                  <td className="px-4 py-3">Copy high-variance traders with tight controls.</td>
                  <td className="px-4 py-3">High</td>
                  <td className="px-4 py-3">Experienced degens</td>
                  <td className="px-4 py-3">5–10% sizing cap, stop-loss discipline, strict exposure limits.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-white">Test Mode</td>
                  <td className="px-4 py-3">Simulate strategy before going live.</td>
                  <td className="px-4 py-3">None</td>
                  <td className="px-4 py-3">New users learning</td>
                  <td className="px-4 py-3">Paper-first, review logs, only go live after consistency.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Next steps</h2>
          <p className="text-sm text-dark-300">
            Want the most complete walkthrough? Start here:
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <Link to="/blog/polymarket-copy-trading" className="text-primary-300 hover:text-primary-200">
                Polymarket Copy Trading Basics (BetMimic)
              </Link>
            </li>
            <li>
              <Link to="/blog/polymarket-risk-management" className="text-primary-300 hover:text-primary-200">
                Risk Management for Polymarket Copy Trading
              </Link>
            </li>
            <li>
              <Link to="/blog/polymarket-copy-trading-playbook" className="text-primary-300 hover:text-primary-200">
                The Ultimate Copy Trading Playbook
              </Link>
            </li>
          </ul>
        </section>

        <section className="text-xs text-dark-500">
          Disclaimer: Trading on prediction markets involves risk, including the potential loss of capital. Always do your
          own research.
        </section>
      </main>
    </div>
  );
}
